import { z } from "zod";

export const PLATFORMS = [
  "后端/API/数据库",
  "PC管理后台",
  "Web/H5",
  "微信小程序",
  "支付宝/抖音小程序",
  "Android原生",
  "iOS原生",
  "HarmonyOS",
  "同源小程序迁移",
  "跨端额外目标平台",
] as const;

export const COMPLEXITIES = ["常规业务", "复杂业务", "高复杂业务"] as const;
export const QUICK_SIZES = ["XS", "S", "M", "L", "手动"] as const;
export const PHASES = ["需求澄清", "技术设计", "开发构建", "自测与修复", "部署与交付"] as const;
export const DELIVERY_FACTORS = ["工期压缩20%-30%", "工期压缩超过30%", "夜间周末紧急", "旧项目接手", "现场工作"] as const;
export const DEMAND_STATUSES = ["清晰", "少量不确定", "不清晰"] as const;

export type Platform = (typeof PLATFORMS)[number];
export type Complexity = (typeof COMPLEXITIES)[number];
export type QuickSize = (typeof QUICK_SIZES)[number];
export type Phase = (typeof PHASES)[number];
export type DeliveryFactor = (typeof DELIVERY_FACTORS)[number];
export type DemandStatus = (typeof DEMAND_STATUSES)[number];

export const WbsItemSchema = z.object({
  id: z.string().min(1),
  module: z.string(),
  feature: z.string(),
  phase: z.enum(PHASES),
  platform: z.enum(PLATFORMS),
  estimateBasis: z.enum(["编码人日", "完整人日"]),
  quickSize: z.enum(QUICK_SIZES),
  inputDays: z.number().min(0),
  complexity: z.enum(COMPLEXITIES),
  deliveryFactors: z.array(z.enum(DELIVERY_FACTORS)),
  notes: z.string(),
});

export const AddonItemSchema = z.object({
  id: z.string().min(1),
  category: z.string(),
  item: z.string(),
  supplyMode: z.string(),
  costAmount: z.number().min(0),
  customerAmount: z.number().min(0),
  included: z.boolean(),
  notes: z.string(),
});

export const MonthlySupportSchema = z.object({
  month: z.string(),
  usedDays: z.number().min(0),
  extraHours: z.number().min(0),
  specialFactor: z.number().min(1),
});

export const PricingSettingsSchema = z.object({
  dayRate: z.number().positive(),
  redlineRate: z.number().positive(),
  hourlyRate: z.number().positive(),
  minimumHours: z.number().positive(),
  monthlyFee: z.number().positive(),
  monthlyIncludedDays: z.number().positive(),
  monthlyExcessDayRate: z.number().positive(),
  fixedMinimum: z.number().positive(),
  reserveClear: z.number().min(0),
  reserveUncertain: z.number().min(0),
  specialFactorCap: z.number().min(1),
  warrantyDays: z.number().positive(),
  validityDays: z.number().positive(),
  splitDaysThreshold: z.number().positive(),
  splitAmountThreshold: z.number().positive(),
  codingShare: z.number().gt(0).lte(1),
  maintenanceBasicRate: z.number().min(0),
  maintenanceBasicMinimum: z.number().min(0),
  maintenanceBasicHours: z.number().min(0),
  maintenanceStandardRate: z.number().min(0),
  maintenanceStandardMinimum: z.number().min(0),
  maintenanceStandardHours: z.number().min(0),
  platformCoefficients: z.record(z.string(), z.number().positive()),
  complexityCoefficients: z.record(z.string(), z.number().positive()),
  deliveryCoefficients: z.record(z.string(), z.number().positive()),
  quickSizeDays: z.record(z.string(), z.number().min(0)),
});

export const PricingProjectSchema = z.object({
  id: z.string().min(1),
  quoteNumber: z.string(),
  clientName: z.string(),
  projectName: z.string(),
  description: z.string(),
  contact: z.string(),
  quoteDate: z.string(),
  demandStatus: z.enum(DEMAND_STATUSES),
  discoveryDays: z.number().min(2).max(5),
  discountRate: z.number().min(0).lt(1),
  taxRate: z.number().min(0),
  maintenanceOption: z.enum(["无", "基础维护", "标准维护"]),
  notes: z.string(),
  wbsItems: z.array(WbsItemSchema),
  addonItems: z.array(AddonItemSchema),
  monthlySupport: MonthlySupportSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const PricingBackupV1Schema = z.object({
  kind: z.literal("outter-demo-calc-backup"),
  schemaVersion: z.literal(1),
  exportedAt: z.string(),
  settings: PricingSettingsSchema,
  projects: z.array(PricingProjectSchema).min(1),
  activeProjectId: z.string(),
});

export const PricingProjectExportV1Schema = z.object({
  kind: z.literal("outter-demo-calc-project"),
  schemaVersion: z.literal(1),
  exportedAt: z.string(),
  project: PricingProjectSchema,
});

export type WbsItem = z.infer<typeof WbsItemSchema>;
export type AddonItem = z.infer<typeof AddonItemSchema>;
export type MonthlySupport = z.infer<typeof MonthlySupportSchema>;
export type PricingSettings = z.infer<typeof PricingSettingsSchema>;
export type PricingProject = z.infer<typeof PricingProjectSchema>;
export type PricingBackupV1 = z.infer<typeof PricingBackupV1Schema>;
export type PricingProjectExportV1 = z.infer<typeof PricingProjectExportV1Schema>;

export type AppState = {
  settings: PricingSettings;
  projects: PricingProject[];
  activeProjectId: string;
  savedAt: string;
};

export type QuoteStatus = "可报价" | "禁止报价" | "仅需求梳理" | "需拆分阶段" | "需调整交付" | "缺少工作量";
